import { FCElement } from '@fc/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@fc/utilities';
import { trapFocus, removeTrapFocus } from '@fc/focus';

const viewportLockSurfaces = new Set();
const overflowRestore = new WeakMap();
const clipPathRestore = new WeakMap();
const DRAWER_SWITCH_EVENT = 'fit:drawer-close-immediate';
export const HEADER_SURFACE_STATE_EVENT = 'fit:header-surface-state';

let lockedScrollY = null;
let lockedDocumentMinHeightRestore = null;

function preserveLockedDocumentHeight(surface) {
  if (!surface.hasAttribute('data-preserve-document-height') || lockedDocumentMinHeightRestore) return;

  const root = document.documentElement;
  const lockedDocumentHeight = Math.max(root.scrollHeight, document.body.scrollHeight);

  lockedDocumentMinHeightRestore = {
    value: root.style.getPropertyValue('min-height'),
    priority: root.style.getPropertyPriority('min-height'),
  };
  root.style.setProperty('min-height', `${lockedDocumentHeight}px`);
}

function restoreLockedDocumentHeight() {
  if (!lockedDocumentMinHeightRestore) return;

  const { value, priority } = lockedDocumentMinHeightRestore;
  lockedDocumentMinHeightRestore = null;

  if (value) {
    document.documentElement.style.setProperty('min-height', value, priority);
  } else {
    document.documentElement.style.removeProperty('min-height');
  }
}

function applyViewportLock() {
  if (viewportLockSurfaces.size === 0) return;
  if (lockedScrollY == null) {
    const scrollY = window.scrollY;
    lockedScrollY = Number.isFinite(scrollY) && scrollY >= 0 ? scrollY : 0;
  }

  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
}

function isSurfaceOpen(surface) {
  if (surface instanceof HTMLDialogElement || surface instanceof HTMLDetailsElement) {
    return surface.open;
  }

  return false;
}

function isWithinSurface(eventTarget, elements) {
  if (!(eventTarget instanceof Node)) return false;

  return elements.some((element) => element instanceof Node && element.contains(eventTarget));
}

function getHeaderSurfaceContract(candidate) {
  if (!(candidate instanceof Element)) return null;

  const owner = candidate.matches('[data-header-surface]')
    ? candidate
    : candidate.closest('[data-header-surface]');

  if (!(owner instanceof HTMLElement)) return null;

  const surfaceId = owner.dataset.headerSurface?.trim();
  if (!surfaceId) return null;

  return {
    owner,
    surfaceId,
    backdropMode: owner.dataset.headerSurfaceBackdrop || 'none',
  };
}

export function releaseViewportLock(surface) {
  if (surface instanceof HTMLElement) {
    viewportLockSurfaces.delete(surface);
  }

  if (viewportLockSurfaces.size > 0) return;

  const scrollY = lockedScrollY ?? 0;
  lockedScrollY = null;

  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  restoreLockedDocumentHeight();

  if (Number.isFinite(scrollY) && scrollY >= 0) {
    window.scrollTo({ top: scrollY, behavior: 'instant' });
  }
}

export function reserveViewportLock(surface) {
  if (!(surface instanceof HTMLElement)) return;
  preserveLockedDocumentHeight(surface);
  viewportLockSurfaces.add(surface);
  applyViewportLock();
}

export function requestDrawerSwitch(except) {
  document.dispatchEvent(new CustomEvent(DRAWER_SWITCH_EVENT, { detail: { except } }));
}

export function dispatchHeaderSurfaceState({ source, owner, phase }) {
  if (!phase) return null;

  const contract = getHeaderSurfaceContract(source) || getHeaderSurfaceContract(owner);
  if (!contract) return null;

  const detail = {
    phase,
    surfaceId: contract.surfaceId,
    backdropMode: contract.backdropMode,
    owner: owner instanceof HTMLElement ? owner : contract.owner,
    source: contract.owner,
  };

  document.dispatchEvent(new CustomEvent(HEADER_SURFACE_STATE_EVENT, { detail }));
  return detail;
}

export function getDetailsSummary(details) {
  if (!(details instanceof HTMLDetailsElement)) return null;

  const summary = details.querySelector(':scope > summary');
  return summary instanceof HTMLElement ? summary : null;
}

export function getDetailsPanel(details) {
  if (!(details instanceof HTMLDetailsElement)) return null;

  const panel = details.querySelector(':scope > [data-drawer-panel]');
  return panel instanceof HTMLElement ? panel : null;
}

export function syncDetailsDisclosure(details, expanded) {
  const summary = getDetailsSummary(details);
  if (!(summary instanceof HTMLElement)) return;

  summary.setAttribute('aria-expanded', String(expanded));

  const panelId = getDetailsPanel(details)?.id;
  if (panelId) {
    summary.setAttribute('aria-controls', panelId);
  }
}

export function bindSurfaceSession({
  host,
  surface,
  trapTarget = surface,
  initialFocus,
  onEscape,
  closeOnOutsideClick = false,
  onOutsideClick,
  trapFocusWithin = true,
  focusInitial = trapFocusWithin,
  preventFocusScroll = false,
}) {
  if (!(host instanceof EventTarget) || !(surface instanceof HTMLElement)) return null;

  const controller = new AbortController();
  const { signal } = controller;

  if (typeof onEscape === 'function') {
    host.addEventListener(
      'keydown',
      (event) => {
        if (!(event instanceof KeyboardEvent) || event.key !== 'Escape') return;
        event.preventDefault();
        onEscape(event);
      },
      { passive: false, signal },
    );
  }

  if (closeOnOutsideClick && typeof onOutsideClick === 'function') {
    document.addEventListener(
      'click',
      (event) => {
        if (!isSurfaceOpen(surface)) return;
        if (isWithinSurface(event.target, [surface, host instanceof Node ? host : null])) return;
        onOutsideClick(event);
      },
      { passive: true, signal },
    );
  }

  if (trapFocusWithin && trapTarget instanceof HTMLElement) {
    requestAnimationFrame(() => {
      if (!isSurfaceOpen(surface)) return;
      trapFocus(trapTarget, { initialFocus, preventScroll: preventFocusScroll });
    });
  } else if (focusInitial && initialFocus instanceof HTMLElement && surface.contains(initialFocus)) {
    requestAnimationFrame(() => {
      if (!isSurfaceOpen(surface)) return;
      initialFocus.focus({ preventScroll: preventFocusScroll });
    });
  }

  return controller;
}

function restoreInlineStyle(map, element, property) {
  if (!map.has(element)) return;

  const value = map.get(element);
  map.delete(element);

  if (value) {
    element.style.setProperty(property, value);
  } else {
    element.style.removeProperty(property);
  }
}

export function setOverlayClipping(elements, isExpanded) {
  const targets = Array.isArray(elements) ? elements : [elements];

  for (const target of targets) {
    if (!(target instanceof HTMLElement)) continue;

    if (isExpanded) {
      if (!overflowRestore.has(target)) {
        overflowRestore.set(target, target.style.getPropertyValue('overflow'));
      }

      target.style.setProperty('overflow', 'visible');

      if (target instanceof HTMLDialogElement) {
        if (!clipPathRestore.has(target)) {
          clipPathRestore.set(target, target.style.getPropertyValue('clip-path'));
        }

        target.style.setProperty('clip-path', 'none', 'important');
      }

      continue;
    }

    restoreInlineStyle(overflowRestore, target, 'overflow');

    if (target instanceof HTMLDialogElement) {
      restoreInlineStyle(clipPathRestore, target, 'clip-path');
    }
  }
}

function syncRootScrollLock() {
  const hasOpenSurface =
    document.querySelector('dialog[open][scroll-lock]') instanceof HTMLElement ||
    document.querySelector('details[open][scroll-lock]') instanceof HTMLElement;

  document.documentElement.toggleAttribute('scroll-lock', hasOpenSurface);
}

function buildDialogDetail(dialog, detail = {}) {
  return {
    dialog: dialog instanceof HTMLDialogElement ? dialog : null,
    ...detail,
  };
}

export class DialogComponent extends FCElement {
  requiredRefs = ['dialog'];
  #restoreFocusTo = null;
  #openController = null;
  #attachedSurfaceCapable = false;
  #defaultAriaModal = null;

  #launcherElements() {
    const dialog = this.refs.dialog;
    const dialogId = dialog instanceof HTMLDialogElement ? dialog.id : '';
    const ownerId = this.id || '';
    const controlledIds = new Set([dialogId, ownerId].filter(Boolean));
    const candidateSelector = '[aria-haspopup="dialog"][aria-controls], [data-ref="trigger"][aria-controls]';
    const localCandidates = Array.from(this.querySelectorAll(candidateSelector));
    const candidates = [
      ...localCandidates,
      ...Array.from(document.querySelectorAll(candidateSelector)),
    ];
    const localCandidateSet = new Set(localCandidates);

    return Array.from(new Set(candidates)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      const controlledId = element.getAttribute('aria-controls') || '';
      return controlledIds.size ? controlledIds.has(controlledId) : localCandidateSet.has(element);
    });
  }

  #syncLauncherState(expanded) {
    for (const launcher of this.#launcherElements()) {
      launcher.setAttribute('aria-expanded', String(Boolean(expanded)));
    }
  }

  #getInitialFocus(dialog) {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const preferredFocus = dialog.querySelector('[data-dialog-initial-focus]');
    if (preferredFocus instanceof HTMLElement) return preferredFocus;

    const activeElement = document.activeElement;
    return activeElement instanceof HTMLElement && dialog.contains(activeElement) ? activeElement : undefined;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#attachedSurfaceCapable =
      this.refs.dialog instanceof HTMLDialogElement && this.refs.dialog.hasAttribute('data-fit-header-surface-dialog');
    this.#defaultAriaModal =
      this.refs.dialog instanceof HTMLDialogElement ? this.refs.dialog.getAttribute('aria-modal') : null;
    this.#syncAttachedSurfaceMode();
    this.#syncLauncherState(false);

    if (this.#attachedSurfaceCapable || this.hasAttribute('data-close-on-resize') || this.minWidth || this.maxWidth) {
      this.addManagedEventListener(window, 'resize', this.#onResize, { passive: true });
    }

    this.addManagedEventListener(document, 'fit:drawer-close-immediate', this.#onDrawerSwitch);
    this.#openForInitialContactFormStatus();
  }

  #openForInitialContactFormStatus() {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    const status = dialog.querySelector('[data-fit-contact-form-status]');
    if (!(status instanceof HTMLElement)) return;

    setTimeout(() => {
      if (!dialog.isConnected || dialog.open) return;
      if (!dialog.querySelector('[data-fit-contact-form-status]')) return;

      this.showDialog({ reason: 'contact-form-status' });
    }, 0);
  }

  disconnectedCallback() {
    if (this.refs.dialog?.open) {
      dispatchHeaderSurfaceState({ source: this.refs.dialog, owner: this, phase: 'deactivate' });
    }

    this.#removeDialogListeners();
    removeTrapFocus(this.refs.dialog);
    releaseViewportLock(this.refs.dialog);
    this.#restoreFocusTo = null;
    super.disconnectedCallback();
  }

  // Close dialog when window moves outside width constraints
  #onResize = debounce(() => {
    const { dialog } = this.refs;
    const wasAttached =
      dialog instanceof HTMLDialogElement && dialog.classList.contains('fit-header-surface-dialog--attached');
    const isAttached = this.#syncAttachedSurfaceMode();

    if (dialog?.open && (this.hasAttribute('data-close-on-resize') || wasAttached !== isAttached)) {
      void this.closeDialog({ restoreFocus: false, immediate: true, reason: 'viewport-change' });
      return;
    }

    const { minWidth, maxWidth } = this;
    if (!minWidth && !maxWidth) return;

    const width = window.innerWidth;
    if (!Number.isFinite(width) || width < 0) return;

    if ((Number.isFinite(minWidth) && width < minWidth) || (Number.isFinite(maxWidth) && width > maxWidth)) {
      void this.closeDialog({ reason: 'viewport-change' });
    }
  }, 50);

  // Header-attached surfaces switch to non-modal desktop behavior only when
  // their dialog explicitly opts into the header-surface contract.
  get #isBarMode() {
    return this.#syncAttachedSurfaceMode();
  }

  get #headerStyle() {
    const headerHost =
      this.closest('site-header[data-header-style]') ||
      this.closest('.header[data-header-style]') ||
      this.refs.dialog?.closest('site-header[data-header-style], .header[data-header-style]');

    return headerHost instanceof HTMLElement ? headerHost.dataset.headerStyle || '' : '';
  }

  #syncAttachedSurfaceMode() {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || !this.#attachedSurfaceCapable) return false;

    const shouldAttach = window.innerWidth >= 750 && this.#headerStyle === 'bar';

    dialog.classList.toggle('fit-header-surface-dialog--attached', shouldAttach);
    dialog.classList.toggle('fit-header-attached-surface', shouldAttach);
    dialog.classList.toggle('fit-header-surface-placement--interactive', shouldAttach);

    if (this.#defaultAriaModal !== null) {
      if (shouldAttach) {
        dialog.removeAttribute('aria-modal');
      } else {
        dialog.setAttribute('aria-modal', this.#defaultAriaModal);
      }
    }

    return shouldAttach;
  }

  #removeDialogListeners() {
    this.#openController?.abort();
    this.#openController = null;
  }

  // Immediately close this dialog when another drawer is opening.
  #onDrawerSwitch = (event) => {
    if (event.detail?.except === this) return;
    if (!this.refs.dialog?.open) return;
    void this.closeDialog({ restoreFocus: false, immediate: true, reason: 'drawer-switch' });
  };

  showDialog(detail = {}) {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    if (document.activeElement instanceof HTMLElement && !dialog.contains(document.activeElement)) {
      this.#restoreFocusTo = document.activeElement;
    }

    const forceModal = this.hasAttribute('force-modal');
    const barMode = !forceModal && this.#isBarMode;
    const preventFocusScroll = !barMode && dialog.hasAttribute('data-preserve-document-height');
    const wasInert = dialog.inert;

    if (!barMode) {
      reserveViewportLock(dialog);
    }

    dispatchHeaderSurfaceState({ source: dialog, owner: this, phase: 'activate' });

    // Close any other open drawers before opening this one
    requestDrawerSwitch(this);

    if (preventFocusScroll) dialog.inert = true;

    try {
      if (barMode) {
        // Non-modal open: no ::backdrop, no inertness — header buttons stay clickable.
        // html[scroll-lock] (via the toggle event) still prevents background scrolling.
        dialog.show();
      } else {
        dialog.showModal();
      }
    } catch (error) {
      dispatchHeaderSurfaceState({ source: dialog, owner: this, phase: 'deactivate' });
      if (!barMode) {
        releaseViewportLock(dialog);
      }
      this.#syncLauncherState(false);
      throw error;
    } finally {
      if (preventFocusScroll) dialog.inert = wasInert;
    }

    this.#syncLauncherState(true);
    this.dispatchEvent(new DialogOpenEvent(buildDialogDetail(dialog, { mode: barMode ? 'bar' : 'modal', ...detail })));

    // Defer listeners to avoid race condition when called inside a click handler
    setTimeout(() => {
      if (!dialog.open) return;

      this.#removeDialogListeners();
      this.#openController = bindSurfaceSession({
        host: this,
        surface: dialog,
        trapTarget: dialog,
        initialFocus: this.#getInitialFocus(dialog),
        trapFocusWithin: !barMode,
        preventFocusScroll,
        onEscape: () => void this.closeDialog({ reason: 'escape-key' }),
        closeOnOutsideClick: !barMode,
        onOutsideClick: barMode ? undefined : () => void this.closeDialog({ reason: 'outside-click' }),
      });
      const signal = this.#openController?.signal;
      if (!signal) return;

      if (!barMode) {
        this.addEventListener('click', this.#onClick, { passive: true, signal });
        dialog.addEventListener('click', this.#onDialogClick, { passive: true, signal });
      }
    }, 0);
  }

  closeDialog = async (options = {}) => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const { restoreFocus = true, immediate = false, reason = 'dismiss' } = options;

    this.#removeDialogListeners();
    removeTrapFocus(dialog);

    if (!immediate) {
      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, { subtree: false });
    }

    releaseViewportLock(dialog);

    dialog.close();
    this.#syncLauncherState(false);
    dispatchHeaderSurfaceState({ source: dialog, owner: this, phase: 'deactivate' });
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent(buildDialogDetail(dialog, { reason, restoreFocus })));

    if (restoreFocus && this.#restoreFocusTo?.isConnected) {
      this.#restoreFocusTo.focus({ preventScroll: dialog.hasAttribute('data-preserve-document-height') });
    }

    this.#restoreFocusTo = null;
  };

  toggleDialog = () => {
    const { dialog } = this.refs;
    if (!dialog) return;
    dialog.open ? void this.closeDialog({ reason: 'toggle' }) : this.showDialog({ reason: 'toggle' });
  };

  #onClick = (event) => {
    const { dialog } = this.refs;
    if (dialog && isClickedOutside(event, dialog)) {
      void this.closeDialog({ reason: 'outside-click' });
    }
  };

  // Backdrop click handler — listens directly on the <dialog> element.
  // When showModal() is used, clicking the ::backdrop fires a click on
  // the dialog with coordinates outside its visible content rect.
  #onDialogClick = (event) => {
    if (event.target !== this.refs.dialog) return;
    const rect = this.refs.dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) {
      void this.closeDialog({ reason: 'backdrop-click' });
    }
  };

  get minWidth() {
    const value = Number(this.getAttribute('dialog-active-min-width'));
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  get maxWidth() {
    const value = Number(this.getAttribute('dialog-active-max-width'));
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }
}

const DIALOG_TAG = 'overlay-dialog';
if (!customElements.get(DIALOG_TAG)) {
  customElements.define(DIALOG_TAG, DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor(detail = {}) {
    super(DialogOpenEvent.eventName, {
      detail,
      bubbles: true,
      composed: true,
    });
  }
  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor(detail = {}) {
    super(DialogCloseEvent.eventName, {
      detail,
      bubbles: true,
      composed: true,
    });
  }
  static eventName = 'dialog:close';
}

// Toggle scroll-lock on <html> when dialog/details elements open or close.
document.addEventListener(
  'toggle',
  (event) => {
    const element = event?.target;
    if (!(element instanceof HTMLDialogElement) && !(element instanceof HTMLDetailsElement)) return;
    if (!element.hasAttribute('scroll-lock')) return;

    requestAnimationFrame(syncRootScrollLock);
  },
  { capture: true, passive: true },
);
